import * as THREE from 'three';
import { base64ToBinary, binaryToBase64 } from 'scripts/base-64';
import sceneryShader from 'scripts/shaders/geo-planet/scenery-shader';
import ColorPalette from '../color-palette';
import { Terrain } from './terrain';
import Engine, { LayerDefinitions } from 'scripts/engine/engine';
import { getModel, ModelPacks } from 'scripts/model-loader';
import { CallbackSet, noise } from 'scripts/engine/helpers';
// Scenery.ts
import { UNIQUE_VERTICES, UNIQUE_FACES, COLOR_PALETTE_SIZE } from './settings';

// Utility
const ceilLog2 = (x: number) => Math.ceil(Math.log2(Math.max(1, x)));

const LOCATION_ID_BITS = ceilLog2(UNIQUE_VERTICES + UNIQUE_FACES); // 13 when n=4 (2562+5120)
const OBJECT_ID_BITS = 6;  // keep as-is unless you need >64 object types
const COLOR_ID_BITS  = ceilLog2(COLOR_PALETTE_SIZE); // 3 when palette size = 8
const SERIALIZED_INSTANCE_SIZE_BITS = OBJECT_ID_BITS + LOCATION_ID_BITS + COLOR_ID_BITS;

interface SceneryInstance {
    objectId: number;
    colorId: number;
    locationId: number;
}

export class Scenery {
    terrain: Terrain;
    colorPalette: ColorPalette;
    material: THREE.Material;
    scene: THREE.Scene;
    locationSceneryMap = new Map<number, SceneryInstance>();
    sceneryMeshMap = new Map<number, THREE.InstancedMesh>();
    modelPack = ModelPacks[0];
    onBeforeChange: CallbackSet;
    onAfterChange: CallbackSet;

    constructor(terrain: Terrain, colorPalette: ColorPalette) {
        this.terrain = terrain;
        this.terrain.onAfterChange.addListener(this, () => {
            this.refresh();
        });
        this.colorPalette = colorPalette;
        this.colorPalette.onAfterChange.addListener(this, (colors: THREE.Color[]) => {
            this.onColorPaletteChange(colors);
        })
        this.scene = new THREE.Scene();
        this.locationSceneryMap = new Map<number, SceneryInstance>();
        this.sceneryMeshMap = new Map<number, THREE.InstancedMesh>();
        this.material = new THREE.ShaderMaterial({
            uniforms: {
                colorPalette: {value: this.colorPalette.colors},
                cameraDirection: {value: new THREE.Vector3()},
                ambientLight: {value: 0.66}
            },
            vertexColors: true,
            vertexShader : sceneryShader.vertex,
            fragmentShader : sceneryShader.fragment
        });
        this.onBeforeChange = new CallbackSet();
        this.onAfterChange = new CallbackSet();
    }

    emitBeforeUpdate() {
        this.onBeforeChange.call();
    }

    emitAfterUpdate() {
        this.onAfterChange.call();
    }

    get count(): number {
        return this.locationSceneryMap.size;
    }

    onColorPaletteChange(colors: THREE.Color[]) {
        const paletteArr = [] as number[];
        colors.forEach(c => {
            paletteArr.push(...c.toArray());
        });
        (this.material as any).uniforms.colorPalette.value = paletteArr;
    }

    refreshColorPalette() {
        const paletteArr = [] as number[];
        this.colorPalette.colors.forEach(c => {
            paletteArr.push(...c.toArray());
        });
        (this.material as any).uniforms.colorPalette.value = paletteArr;
    }

    animate() {
        (this.material as any).uniforms.cameraDirection.value = Engine.instance.cameraDirection;
    }

    addScenery(objectId: number, colorId: number, locationIds: number | number[]) {
        this.emitBeforeUpdate();
        if (Array.isArray(locationIds)) {
            let needsRefresh = false;
            locationIds.forEach(locationId => {
                if (this._addScenery(objectId, colorId, locationId)) {
                    needsRefresh = true;
                }
            });
            if (needsRefresh) {
                this.refresh();
            }
        } else {
            if (this._addScenery(objectId, colorId, locationIds)) {
                this.refresh();
            }
        }
        this.emitAfterUpdate();
    }

    _addScenery(objectId: number, colorId: number, locationId: number) {
        let needsRefresh = false;
        const current = this.locationSceneryMap.get(locationId);
        if (!current || (current.objectId !== objectId || current.colorId !== colorId || current.locationId !== locationId)) {
            needsRefresh = true;
        }
        this.locationSceneryMap.set(locationId, {
            objectId: objectId,
            colorId: colorId,
            locationId: locationId
        });
        return needsRefresh;
    }

    removeScenery(locationIds: number | number[]) {
        this.emitBeforeUpdate();
        if (Array.isArray(locationIds)) {
            let needsRefresh = false;
            locationIds.forEach(locationId => {
                if(this.locationSceneryMap.delete(locationId)) {
                    needsRefresh = true;
                }
            });
            if (needsRefresh) {
                this.refresh();
            }
        } else {
            if(this.locationSceneryMap.delete(locationIds)) {
                this.refresh();
            }
        }
        this.emitAfterUpdate();
    }

    refresh() {
        this.modelPack.models.forEach(model => this.spawnInstancedObjects(model.id));
    }

    spawnInstancedObjects(objectId: number) {
        const model = this.modelPack.models.find(model => model.id === objectId);
        if (!model) {
            return;
        }
        const objectInstances = Array.from(this.locationSceneryMap.values()).filter(o => o.objectId === objectId);
        if (objectInstances.length === 0) {
            const oldInstancedMesh = this.sceneryMeshMap.get(objectId);
            if (oldInstancedMesh) {
                oldInstancedMesh.dispose();
                this.scene.remove(oldInstancedMesh);
            }
            return;
        }
        const geometry = getModel(this.modelPack.id, model.id);
        if (geometry) {
            geometry.computeVertexNormals();
            const objectMesh = new THREE.InstancedMesh(
                geometry,
                this.material,
                objectInstances.length
            );
            objectMesh.updateMatrixWorld();
            const quaternion = new THREE.Quaternion();
            const transform = new THREE.Object3D();
            objectInstances.forEach((objInstance, index) => {
                const objectPosition = this.terrain.locationIdToPoint(objInstance.locationId);
                const pos = new THREE.Vector3(objectPosition.x, objectPosition.y, objectPosition.z);
                transform.position.set(...pos.toArray());
                pos.normalize();
                transform.setRotationFromQuaternion(quaternion.setFromUnitVectors(new THREE.Vector3(0,1,0), pos));
                transform.rotateOnWorldAxis(pos, 2 * Math.PI * noise(objInstance.locationId));
                transform.updateMatrix();
                objectMesh.setMatrixAt( index, transform.matrix );
                const colFloat = objInstance.colorId / COLOR_PALETTE_SIZE;
                objectMesh.setColorAt(index, new THREE.Color(colFloat, colFloat, colFloat));
            });

            const oldInstancedMesh = this.sceneryMeshMap.get(objectId);
            if (oldInstancedMesh) {
                oldInstancedMesh.dispose();
                this.scene.remove(oldInstancedMesh);
            }
            objectMesh.layers.set(LayerDefinitions.default);
            this.sceneryMeshMap.set(model.id, objectMesh);
            this.scene.add(objectMesh);
        }
    }

    serialize() {
        let binary = Array.from(this.locationSceneryMap.values()).map(scenery => {
            return scenery.objectId.toString(2).padStart(OBJECT_ID_BITS, '0') +
                   scenery.locationId.toString(2).padStart(LOCATION_ID_BITS, '0') +
                   scenery.colorId.toString(2).padStart(COLOR_ID_BITS, '0');
        }).join('');

        // Model pack id (still 6 bits if you like)
        binary = (0).toString(2).padStart(6, '0') + binary;
        return binaryToBase64(binary);
    }

    deserialize(base64: string) {
        const binary = base64ToBinary(base64);
        if (binary.length < 6) return false;

        // const modelPackBinary = binary.substring(0, 6);
        const sceneryBinary = binary.substring(6);

        this.locationSceneryMap.clear();
        const size = SERIALIZED_INSTANCE_SIZE_BITS;
        const itemCount = Math.floor(sceneryBinary.length / size);

        for (let i = 0; i < itemCount; i++) {
            const start = i * size;
            const end   = start + size;
            const chunk = sceneryBinary.substring(start, end);

            let offset = 0;
            const objectId   = parseInt(chunk.substring(offset, offset + OBJECT_ID_BITS), 2);
            offset += OBJECT_ID_BITS;
            const locationId = parseInt(chunk.substring(offset, offset + LOCATION_ID_BITS), 2);
            offset += LOCATION_ID_BITS;
            const colorId    = parseInt(chunk.substring(offset, offset + COLOR_ID_BITS), 2);

            this.locationSceneryMap.set(locationId, { objectId, locationId, colorId });
        }
        this.refreshColorPalette();
        this.refresh();
        return true;
    }

    dispose() {
        Array.from(this.sceneryMeshMap.values()).forEach(mesh => {
            this.scene.remove(mesh);
            mesh.dispose();
        });
        this.material.dispose();
    }
}

export default Scenery;
